import { ChevronRightIcon } from 'lucide-react';
import { cn } from '@liza/ui/utils';
import { Item, ItemActions, ItemContent, ItemTitle } from '../ui/item';
import { type PromotionStatus, RecordPromotionButton } from './RecordPromotionButton';

export type { PromotionStatus } from './RecordPromotionButton';

export type RecordListItemProps = {
  title: string;
  /** Short marker rendered before the title in a muted badge — e.g. how many records collapsed into
   * this row. Omit for the ordinary single-record case. */
  titlePrefix?: string;
  description?: React.ReactNode;
  onClick?: () => void;
  onPromotionClick?: () => void;
  promotionStatus?: PromotionStatus;
  selected?: boolean;
  /** Optional `data-testid` for the row. Callers set this to give tests a stable hook. */
  testId?: string;
};

export const RecordListItem = ({
  title,
  titlePrefix,
  description,
  onClick,
  onPromotionClick,
  promotionStatus,
  selected,
  testId,
}: RecordListItemProps): React.ReactElement => {
  return (
    <Item
      size="sm"
      data-testid={testId}
      className={cn(
        onClick && 'cursor-pointer hover:bg-accent/50',
        onClick && selected && 'bg-accent text-accent-foreground hover:bg-accent',
      )}
      onClick={onClick}
      aria-current={onClick && selected ? 'true' : undefined}
    >
      <ItemContent>
        <ItemTitle className="flex items-center gap-1.5 text-sm">
          {titlePrefix && (
            <span
              className="shrink-0 rounded-sm bg-muted px-1 py-0.5 text-xs font-medium text-muted-foreground tabular-nums"
              data-testid="record-list-item-count"
            >
              {titlePrefix}
            </span>
          )}
          {title}
        </ItemTitle>
        {description && (
          <span className={cn('text-xs', onClick && selected ? 'text-accent-foreground/70' : 'text-muted-foreground')}>
            {description}
          </span>
        )}
      </ItemContent>

      <ItemActions>
        {onPromotionClick && <RecordPromotionButton onClick={onPromotionClick} status={promotionStatus} />}
        {onClick && <ChevronRightIcon className="size-4 text-muted-foreground" />}
      </ItemActions>
    </Item>
  );
};
